import { createSelector } from "@reduxjs/toolkit";
import { last } from "lodash";

const selectShreddingDetailState = (state) => state?.shredding?.detail;

export const selectIsByParagraph = (state) =>
  Boolean(state?.shredding?.detail?.report?.isByParagraph);

export const selectIsReviewMode = (state) =>
  Boolean(state?.shredding?.detail?.isReviewMode);

export const selectIsLoadingCategories = (state) =>
  Boolean(state?.shredding?.detail?.isLoadingCategories);

export const selectReport = (state) => state?.shredding?.detail?.report || null;

export const selectReportSections = (state) =>
  state?.shredding?.detail?.sections || [];

export const selectIsLoadingTerms = (state) =>
  Boolean(state?.shredding?.detail?.isLoadingTerms);

export const selectSelectedParagraphIds = createSelector(
  [selectShreddingDetailState],
  (detail) => detail?.selectedParagraphIds || []
);

export const selectHiddenParagraphs = createSelector(
  [selectReportSections, selectSelectedParagraphIds],
  (sections, selectedParagraphIds) => {
    if (!selectedParagraphIds.length) {
      return [];
    }

    let result = [];
    let section;
    for (section of sections) {
      const { linkedParagraphs, paragraphId } = section;
      if (!selectedParagraphIds.includes(paragraphId)) {
        result = result.concat(linkedParagraphs);
      }
    }
    return result;
  }
);

export const selectReportCategoriesData = (state) =>
  state?.shredding?.detail?.categories || null;

export const selectParagraphs = createSelector(
  [selectShreddingDetailState],
  (detail) => detail?.paragraphs || []
);

export const selectSentences = createSelector(
  [selectShreddingDetailState],
  (detail) => detail?.sentences || []
);

export const selectReportName = (state) =>
  state?.shredding?.detail?.categories?.nme || "";

export const selectCurrentDictionaryId = (state) =>
  state?.shredding?.detail?.categories?.dict?.id;

export const selectReportCategories = createSelector(
  [selectIsLoadingCategories, selectReportCategoriesData],
  (isLoading, categories) => {
    return isLoading ? [] : categories?.dict?.cats || [];
  }
);

export const selectTermsFrequency = (state) =>
  state?.shredding?.detail?.categories?.fq || 0;

export const selectAllCategoryTerms = createSelector(
  [selectIsLoadingCategories, selectReportCategoriesData],
  (isLoading, data) => {
    if (isLoading) {
      return [];
    }

    const categories = data?.dict?.cats || [];

    return categories.reduce((arr, cat) => {
      return arr.concat(cat.items || []);
    }, []);
  }
);

export const selectReportTerms = createSelector(
  [
    selectIsByParagraph,
    selectIsLoadingTerms,
    selectParagraphs,
    selectSentences,
  ],
  (isByParagraph, isLoading, paragraphs, sentences) => {
    if (isLoading) {
      return [];
    }
    return isByParagraph ? paragraphs : sentences;
  }
);

export const selectReportTermsData = createSelector(
  [selectIsByParagraph, selectParagraphs, selectSentences],
  (isByParagraph, paragraphs, sentences) => {
    return isByParagraph ? paragraphs || null : sentences || null;
  }
);

export const selectLastParagraphIndex = createSelector(
  [selectIsByParagraph, selectParagraphs, selectSentences],
  (isByParagraph, paragraphs, sentences) => {
    const terms = isByParagraph ? paragraphs : sentences;

    if (!terms?.length) {
      return null;
    }

    return parseInt(last(terms).paragraphIndex, 10);
  }
);

export const selectHiddenTerms = createSelector(
  [selectReportSections, selectSelectedParagraphIds],
  (sections, selectedParagraphIds) => {
    if (!selectedParagraphIds.length) {
      return [];
    }

    let hiddenTerms = [];

    for (const section of sections) {
      if (!selectedParagraphIds.includes(section.paragraphId)) {
        hiddenTerms = hiddenTerms.concat(section.sectionTerms || []);
      }
    }
    return hiddenTerms;
  }
);

export const selectTermDescriptionsById = createSelector(
  [selectAllCategoryTerms],
  (allTerms) => {
    const map = {};
    for (const term of allTerms) {
      if (term?.id && term.iDesc) {
        map[term.id] = term.iDesc;
      }
    }
    return map;
  }
);
